import { useState, type ReactNode } from 'react';
import {
  Alert, Box, Card, CircularProgress, IconButton, Menu, MenuItem, Typography,
} from '@mui/material';
import {
  DragIndicator, MoreVert, Edit, ContentCopy, Delete,
} from '@mui/icons-material';
import type { DashboardConfig, DashboardPanel } from './types';
import { usePanelData } from './usePanelData';
import TablePanel from './panels/TablePanel';
import ChartPanel from './panels/ChartPanel';
import StatPanel from './panels/StatPanel';
import SummaryPanel from './panels/SummaryPanel';

function Centered({ children }: { children: ReactNode }) {
  return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</Box>;
}

export default function PanelCard({
  dashboard, panel, editMode, onEdit, onDuplicate, onDelete,
}: {
  dashboard: DashboardConfig;
  panel: DashboardPanel;
  editMode: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { data, isLoading, isError, error, isFetching } = usePanelData(dashboard, panel);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const body = (() => {
    if (panel.viz === 'summary') return <SummaryPanel workspaceId={panel.query?.workspace_id} />;
    if (isLoading) return <Centered><CircularProgress size={20} /></Centered>;
    if (isError) return <Alert severity="error" sx={{ m: 1 }}>{(error as Error)?.message || 'Failed to load'}</Alert>;
    if (!data || (data.rows.length === 0 && panel.viz !== 'stat')) {
      return <Centered><Typography variant="body2" color="text.secondary">No data in range</Typography></Centered>;
    }
    if (panel.viz === 'table') return <TablePanel panel={panel} stats={data} dashboard={dashboard} editMode={editMode} />;
    if (panel.viz === 'stat') return <StatPanel panel={panel} stats={data} />;
    return <ChartPanel panel={panel} stats={data} />;
  })();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider',
      }}
    >
      <Box
        className={editMode ? 'panel-drag-handle' : undefined}
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: 0.5, px: 1.75, pt: 1.25, pb: 0.5,
          cursor: editMode ? 'move' : 'default', userSelect: 'none',
        }}
      >
        {editMode && (
          <DragIndicator sx={{
            mt: 0.25,
            fontSize: 18,
            color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.text.disabled),
          }}
          />
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap title={panel.title}>{panel.title}</Typography>
          {panel.subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {panel.subtitle}
            </Typography>
          )}
        </Box>
        {isFetching && !isLoading && <CircularProgress size={13} sx={{ mt: 0.5 }} />}
        {editMode && (
          <>
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Panel options">
              <MoreVert sx={{ fontSize: 18 }} />
            </IconButton>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
              <MenuItem onClick={() => { setAnchor(null); onEdit(); }}><Edit sx={{ fontSize: 16, mr: 1 }} />Edit</MenuItem>
              <MenuItem onClick={() => { setAnchor(null); onDuplicate(); }}><ContentCopy sx={{ fontSize: 16, mr: 1 }} />Duplicate</MenuItem>
              <MenuItem onClick={() => { setAnchor(null); onDelete(); }}><Delete sx={{ fontSize: 16, mr: 1 }} />Delete</MenuItem>
            </Menu>
          </>
        )}
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, px: panel.viz === 'table' ? 0 : 1.5, pb: panel.viz === 'table' ? 0 : 1, pt: 0.5 }}>{body}</Box>
    </Card>
  );
}
